import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, Ear, Compass } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { usePageTitle } from "@/lib/usePageTitle";

const PORTRAIT_IMG = "/images/brian-about.jpg";
const FOREST_IMG = "https://images.unsplash.com/photo-1600340053706-32d1278206ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHxfYXJtJTIwbmF0dXJlfGVufDB8fHx8MTc5MDMyNDQ4NA&ixlib=rb-4.1.0&q=85";

const VALUES = [
  { icon: Ear, title: "Truly listened to", text: "Therapy is a journey through your own experience — the therapist is simply your guide." },
  { icon: HeartHandshake, title: "Without judgement", text: "Whatever you bring is met with warmth and respect. Nothing is too small, too messy, or too much." },
  { icon: Compass, title: "At your own pace", text: "There is no pressure to go anywhere before you are ready. The direction of the work is agreed together." },
];

const About = () => {
  usePageTitle("About Brian", "About Brian Boland, psychotherapist and counsellor for adults in Marino, Dublin 3 and online.");
  return (
    <div data-testid="about-page">
      <section className="mx-auto max-w-6xl px-5 pb-20 pt-16 sm:px-8 lg:pt-24">
        <div className="grid items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <Reveal y={12}><p className="eyebrow">About</p></Reveal>
            <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.12] tracking-tight text-[#1E2723] sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.15}>A therapist who</MaskedLine>
              <MaskedLine delay={0.3} className="italic text-[#527363]">meets you as a person</MaskedLine>
            </h1>
            <Reveal delay={0.5}>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-[#4A5750] sm:text-lg">
                Brian Boland is a psychotherapist and counsellor working with adults, based in Marino, Dublin 3, and working online with clients throughout Ireland and around the world.
              </p>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-[#4A5750]">
                He works integratively — drawing on humanistic, person-centred and existential thinking — so that the therapy fits you, rather than fitting you into a model. Above all, he believes change begins with being genuinely heard and understood.
              </p>
            </Reveal>
            <Reveal delay={0.65}>
              <Link to="/booking" data-testid="about-book-cta" className="btn-primary mt-9">
                Book a first session <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.3}>
            <div className="arch-frame mx-auto w-full max-w-sm overflow-hidden border border-[#E2DACD] shadow-[0_24px_60px_-16px_rgba(30,39,35,0.25)]">
              <img src={PORTRAIT_IMG} alt="Brian Boland, Psychotherapist and Counsellor" className="h-[480px] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F2ECE1] py-24" aria-labelledby="values-heading">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">How Brian works</p>
            <h2 id="values-heading" className="mt-4 max-w-2xl font-serif text-2xl font-medium leading-snug text-[#1E2723] sm:text-3xl lg:text-4xl">
              A safe, non-judgmental space, shaped around you
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={0.1 * i}>
                <div className="soft-card h-full p-7" data-testid={`value-card-${i}`}>
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#E3ECE7] text-[#527363]">
                    <v.icon size={22} strokeWidth={1.75} />
                  </span>
                  <h3 className="mt-5 text-lg font-semibold text-[#1E2723]">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#54645B]">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-24 sm:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden rounded-2xl border border-[#E2DACD]">
              <img src={FOREST_IMG} alt="Serene forest path in morning light" className="h-80 w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="eyebrow">Qualifications & standards</p>
            <h2 className="mt-4 font-serif text-2xl font-medium leading-snug text-[#1E2723] sm:text-3xl">
              Professional training, human delivery
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#4A5750]">
              Brian holds a BSc in Integrative and Pluralistic Psychotherapy and Counselling from IICP College. He is a member of the Irish Association for Counselling and Psychotherapy (IACP) and is bound by their code of ethics and governance.
            </p>
            <p className="mt-4 text-base leading-relaxed text-[#4A5750]">
              This includes a commitment to ongoing professional supervision and continuing professional development — so the care you receive is accountable, ethical and current.
            </p>
            <Link to="/therapy" data-testid="about-therapy-cta" className="btn-secondary mt-8">
              Explore therapy & counselling <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default About;
